/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from "react";
import "./ExperiencePassesPaymentScreen.css";
import backIcon from "../../assets/images/back-icon.svg";
import paymentRightArrow from "../../assets/images/membership-payment-right-arrow.svg";
import appTextIcon from "../../assets/images/header-sabehub-text-app-icon.svg";
import letsDoItIcon from "../../assets/images/lets-do-it-icon.svg";
import basicPassIcon from "../../assets/images/dfw-icons/dfw-Club.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import SnackbarAlert from "components/elements/SnackbarAlert";
import { CircularProgress } from "@mui/material";
import {
  CALENDAR_RESERVATION_TABS,
  CALENDAR_RESERVATION_TYPE,
  EXPERIENCE_TYPE,
  PAYMENT_STATUS,
  PURCHASE_TYPE,
} from "utils/constants";
import { MESSAGE } from "utils/strings";
import ROUTE_PATHS from "utils/routePaths";
import {
  calculateGSTPrice,
  formatDateString,
  formatRetreatTime,
  formatWorkspaceTimeRange,
  getIsActiveMemberStatus,
  getMembershipPassesIcon,
} from "utils/utils";
import { useStorage } from "services/StorageProvider";
import {
  trackCheckoutStarted,
  trackPaymentSuccess,
  trackCheckoutAbandoned,
  trackEvent,
} from "utils/analytics";

const ExperiencePassesPaymentScreen: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfileData } = useStorage();
  const { fetchEventOffers, reserveWSMSlot } = useWSMApi();
  const [selectedSlotData, setSelectedSlotData] = useState<any>(null);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<string>("");
  const isActiveMember = getIsActiveMemberStatus(userProfileData);
  const [selectedSlotPrice, setSelectedSlotPrice] = useState<number | null>(
    null,
  );
  const [selectedSlotDiscountedPrice, setSelectedSlotDiscountedPrice] =
    useState<number | null>(null);
  const { createPayment, verifyPayment } = useWSMApi();
  const [selectedOffer, setSelectedOffer] = useState<any>(null);

  const { data: offersData } = useQuery({
    queryKey: ["get-event-offers"],
    queryFn: () =>
      fetchEventOffers({
        ids: selectedSlotData?.offers,
      }),
    retry: false,
    enabled: !!selectedSlotData?.offers,
  });

  const slotReservationMutation = useMutation({
    mutationFn: () => reserveWSMSlot({ slot_id: selectedSlotData?.id }),
    onSuccess: () => {
      trackEvent("reservation_created", {
        slot_id: String(selectedSlotData?.id ?? ""),
        service_type: selectedSlotData?.service_type ?? "",
      });
      const paymentData = {
        title: `${selectedSlotData?.title}`,
        type: PURCHASE_TYPE.EVENT,
      };
      localStorage.setItem("paymentSuccessData", JSON.stringify(paymentData));
      if (selectedSlotData?.experience_type === EXPERIENCE_TYPE.FOCUS_HOUSE) {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PASS_SUCCESS_SCREEN, {
          state: { from: location.pathname },
          replace: true,
        });
      } else {
        navigate(ROUTE_PATHS.WSM_ALL_SET, {
          state: { from: location.pathname },
          replace: true,
        });
      }
    },
  });

  const purchaseMembershipMutation = useMutation({
    mutationFn: (data: any) => createPayment(data),
    onSuccess: (response, obj) => {
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: obj.product.total_price,
        currency: obj.currency,
        name: "The Foundry", //Name Shown in Razorpay Form
        description: obj.product.description,
        order_id: response.id, // This is the order_id created in the backend
        prefill: {
          name: obj.customer.name,
          email: obj.customer.email,
          contact: obj.customer.contact,
        },
        theme: {
          color: "#1C1C1E",
        },
        handler: async function (paymentResponse: any) {
          setPaymentId(paymentResponse.razorpay_payment_id);
          trackPaymentSuccess(
            obj.product.total_price / 100,
            paymentResponse.razorpay_payment_id,
            obj.product.description,
            "slot",
          );
          verifyPaymentMutation.mutate({
            razorpay_order_id: paymentResponse.razorpay_order_id,
            razorpay_payment_id: paymentResponse.razorpay_payment_id,
            razorpay_signature: paymentResponse.razorpay_signature,
          });
        },
        modal: {
          ondismiss: () => {
            if (!paymentStatus) {
              setPaymentStatus(PAYMENT_STATUS.CANCELLED);
              trackCheckoutAbandoned("payment");
            }
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    },
  });

  const verifyPaymentMutation = useMutation({
    mutationFn: (data: any) => verifyPayment(data),
    onSuccess: (response) => {
      setPaymentStatus(response.status);
    },
    onError: () => {
      setPaymentStatus(PAYMENT_STATUS.FAILED);
      navigate(-1);
    },
  });

  useEffect(() => {
    const storedSlotData = localStorage.getItem("selectedSlotData");
    if (storedSlotData) {
      const slotData = JSON.parse(storedSlotData);
      setSelectedSlotData(slotData);
      const price = isActiveMember
        ? slotData?.member_slot_price
        : slotData?.non_member_slot_price;
      setSelectedSlotPrice(price);
      setSelectedSlotDiscountedPrice(
        isActiveMember
          ? slotData?.member_discounted_slot_price
          : slotData?.non_member_discounted_slot_price,
      );
      trackCheckoutStarted(Number(price ?? 0));
    }
  }, [isActiveMember]);

  useEffect(() => {
    if (paymentStatus === PAYMENT_STATUS.SUCCESS) {
      const paymentData = {
        title: getPaymentTitle(),
        type: PURCHASE_TYPE.EVENT,
      };
      localStorage.setItem("paymentSuccessData", JSON.stringify(paymentData));
      if (selectedSlotData?.experience_type === EXPERIENCE_TYPE.FOCUS_HOUSE) {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PASS_SUCCESS_SCREEN, {
          state: { from: location.pathname },
          replace: true,
        });
      } else {
        navigate(ROUTE_PATHS.WSM_ALL_SET, {
          state: { from: location.pathname },
          replace: true,
        });
      }
    }
  }, [paymentStatus, paymentId, navigate]);

  const getPaymentTitle = () => {
    let title =
      CALENDAR_RESERVATION_TABS.find(
        (type) => type.value === selectedSlotData?.service_type,
      )?.name || "";
    if (title.endsWith("s")) {
      title = title.slice(0, -1);
    }
    return title;
  };

  const handleBackButtonClick = () => {
    navigate(-1);
  };

  const handleProceedToPaymentClick = () => {
    if (selectedSlotData?.is_free_slot && !selectedOffer) {
      slotReservationMutation.mutate();
    } else {
      const amountInSubunits = Math.round(
        Number(
          selectedSlotData?.is_free_slot
            ? getFreeSlotFinalPrice()
            : getFinalSlotPrice(),
        ) * 100,
      );
      const passedObj = {
        amount: selectedSlotData?.is_free_slot
          ? getFreeSlotFinalPrice()
          : getFinalSlotPrice(),
        currency: "INR",
        customer: {
          name: `${userProfileData?.first_name} ${userProfileData?.last_name}`,
          email: userProfileData?.email,
          contact: userProfileData?.mobile_number,
        },
        product: {
          total_price: amountInSubunits, // Amount in paise
          slot_id: selectedSlotData?.id,
          offer_id: selectedOffer?.id,
          description: selectedSlotData?.title,
          price: getSlotPrice(),
          with_gst_price: getGSTPrice(),
          credit: selectedSlotData?.credits_required,
        },
      };
      setPaymentStatus(null);
      purchaseMembershipMutation.mutate(passedObj);
    }
  };

  const getSlotPrice = () => {
    return selectedSlotDiscountedPrice
      ? selectedSlotDiscountedPrice
      : selectedSlotPrice;
  };

  const getGSTPrice = () => {
    const price = getSlotPrice();
    const slotGST = calculateGSTPrice(
      Number(price),
      selectedSlotData?.slot_gst,
    );
    const offerGST = selectedOffer
      ? calculateGSTPrice(selectedOffer?.price, selectedOffer?.gst)
      : 0;
    return slotGST + offerGST;
  };

  const getSlotPriceWithOffer = () => {
    const slotPrice = getSlotPrice();
    const offerPrice = selectedOffer ? selectedOffer.price : 0;
    return slotPrice + offerPrice;
  };

  const getFinalSlotPrice = () => {
    return getSlotPriceWithOffer() + getGSTPrice();
  };

  const getFreeSlotFinalPrice = () => {
    const offerPrice = selectedOffer ? selectedOffer.price : 0;
    const offerGST = selectedOffer
      ? calculateGSTPrice(selectedOffer?.price, selectedOffer?.gst)
      : 0;
    return offerPrice + offerGST;
  };

  const getFreeSlotDiscount = () => {
    const price = getSlotPrice();
    const slotGST = calculateGSTPrice(
      Number(price),
      selectedSlotData?.slot_gst,
    );
    const slotPrice = getSlotPrice() ?? 0;
    return slotPrice + slotGST;
  };

  return (
    <div className="wms-mobile-container">
      {purchaseMembershipMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {purchaseMembershipMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {slotReservationMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {slotReservationMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {paymentStatus === PAYMENT_STATUS.FAILED ? (
        <SnackbarAlert type={"error"}>{MESSAGE.PAYMENT_FAILED}</SnackbarAlert>
      ) : (
        ""
      )}
      <div className="experience-passes-payment-detail-screen-container">
        <div className="experience-passes-payment-detail-header-container">
          <img
            src={backIcon}
            alt="dropdown-logo"
            className="image"
            onClick={handleBackButtonClick}
          />
          <img src={appTextIcon} alt="header-text-app-icon" />
        </div>
        <div className="experience-passes-payment-detail-tab-bar-container">
          <div
            className="experience-passes-payment-detail-tab-bar-item-container"
            style={{ fontWeight: "700" }}
          >
            <div className="circle-container review">1</div>
            Review
          </div>
          <img src={paymentRightArrow} alt="payment-right-arrow" />
          <div className="experience-passes-payment-detail-tab-bar-item-container">
            <div className="circle-container payment">2</div>
            Payment
          </div>
        </div>
        <div className="experience-passes-payment-detail-card-container">
          <div className="image-text-container">
            <img
              className="card-image"
              src={selectedSlotData?.slot_image}
              alt="membership-card"
            />
            <div className="custom-text-overlay"></div>
          </div>
          <div className="right-container">
            <div className="sub-container">
              {selectedSlotData?.is_free_slot ? (
                <>
                  <div className="free-slot-container">
                    FREE
                    <span>
                      For <span style={{ color: "#C1F700" }}>BETA 30</span>
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="plan-amount-container">
                    {`INR ${getSlotPrice()?.toLocaleString("en-US")}`}
                    <span>Only</span>
                  </div>
                  {selectedSlotDiscountedPrice && (
                    <div className="plan-offer-price-container">
                      {`INR ${selectedSlotPrice?.toLocaleString("en-US")}`}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="total-credit-text">{selectedSlotData?.title}</div>
            <div className="sub-container">
              <div className="valid-up-to-text">
                {formatDateString(selectedSlotData?.start_time)}
              </div>
              <div className="credit-validity-container">
                {selectedSlotData?.service_type ===
                CALENDAR_RESERVATION_TYPE.RETREAT
                  ? formatRetreatTime(selectedSlotData?.start_time)
                  : formatWorkspaceTimeRange(
                      selectedSlotData?.start_time,
                      selectedSlotData?.end_time,
                    )}
              </div>
            </div>
          </div>
        </div>
        <div className="experience-passes-payment-title-container">
          <span className="title">
            Select ur <span style={{ color: "#FF6B35" }}>pass</span>
          </span>
          <span className="sub-title">Clean fuel, Made for Focus.</span>
        </div>
        <div
          className={`experience-passes-payment-detail-offer-container-cell ${
            selectedOffer === null ? "selected" : ""
          }`}
          onClick={() => setSelectedOffer(null)}
        >
          <div className="sub-container">
            <div
              className="image-container"
              style={{
                backgroundImage: `url(${basicPassIcon})`,
                backgroundColor: "black",
                backgroundSize: "100% 100%",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
            ></div>
            <div className="text-container">
              <div className="text-container-header">
                <span className="title" style={{color: "#C1F700"}}>Experience Pass</span>
              </div>
              <span className="discription">Your Experience</span>
            </div>
          </div>
          <span
            className="price-text"
            style={{
              textDecoration: selectedSlotData?.is_free_slot
                ? "line-through"
                : "",
            }}
          >
            ₹{getSlotPrice()?.toLocaleString("en-US")}
          </span>
        </div>
        {offersData?.data?.content?.length > 0 && (
          <div className="experience-passes-payment-detail-offer-container">
            {offersData?.data?.content?.map((offer: any, index: number) => {
              return (
                <div
                  key={offer.id}
                  className={`experience-passes-payment-detail-offer-container-cell ${
                    selectedOffer?.id === offer.id ? "selected" : ""
                  }`}
                  onClick={() =>
                    setSelectedOffer(
                      selectedOffer?.id === offer.id ? null : offer,
                    )
                  }
                >
                  <div className="sub-container">
                    <div
                      className="image-container"
                      style={{
                        backgroundImage: `url(${getMembershipPassesIcon(index)})`,
                        backgroundColor: "black",
                        backgroundSize: "100% 100%",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                      }}
                    ></div>
                    <div className="text-container">
                      <div className="text-container-header">
                        <span className="title">{offer.title}</span>
                      </div>
                      <span className="discription">
                        Your Experience +{" "}
                        <span style={{ color: "white" }}>
                          {offer.description}
                        </span>
                      </span>
                    </div>
                  </div>
                  {offer.tag_title && (
                    <div className="tag-container">{offer.tag_title}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="experience-passes-payment-summary-details-container">
          <div className="payment-detail">
            <span className="bold">
              {selectedOffer ? selectedOffer?.title : "Basic Pass"}
            </span>
            <span className="bold">{`₹ ${getSlotPriceWithOffer()?.toLocaleString("en-US")}`}</span>
          </div>
          <div className="payment-detail" style={{ marginBottom: "4px" }}>
            <span className="semi-bold">
              Your Experience{" "}
              {selectedOffer && <span>+ {selectedOffer?.description}</span>}
            </span>
          </div>
          <div className="divider"></div>
          <div className="payment-detail" style={{ margin: "8px 0" }}>
            <span>GST</span>
            <span>₹ {getGSTPrice()?.toLocaleString("en-US")}</span>
          </div>
          {selectedSlotData?.is_free_slot && (
            <>
              <div className="divider"></div>
              <div className="payment-detail" style={{ margin: "8px 0" }}>
                <span style={{ color: "#C1F700" }}>Discount</span>
                <span>-₹ {getFreeSlotDiscount()?.toLocaleString("en-US")}</span>
              </div>
            </>
          )}
          <div className="divider" style={{ marginBottom: "8px" }}></div>
          <div className="payment-detail">
            <span className="total">Total Payable</span>
            {selectedSlotData?.is_free_slot ? (
              <span className="total">
                {selectedOffer
                  ? `₹ ${getFreeSlotFinalPrice()?.toLocaleString("en-US")}`
                  : "Free"}
              </span>
            ) : (
              <span className="total">{`₹ ${getFinalSlotPrice()?.toLocaleString(
                "en-US",
              )}`}</span>
            )}
          </div>
          <div className="divider"></div>
        </div>
      </div>
      <div
        className="experience-passes-payment-detail-purchase-btn"
        onClick={handleProceedToPaymentClick}
      >
        {purchaseMembershipMutation?.isPending ||
        slotReservationMutation?.isPending ? (
          <CircularProgress size={30} color="inherit" />
        ) : (
          <>
            {selectedSlotData?.is_free_slot && !selectedOffer
              ? "Confirm Now"
              : "Pay Now"}
            <img src={letsDoItIcon} alt="lets-do-it-logo" />
          </>
        )}
      </div>
    </div>
  );
};

export default ExperiencePassesPaymentScreen;
